import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { RecoveryPointDto } from 'src/app/services/dtos'
import { equalSets } from '../utils'

export interface RpSliderChangedEvent {
  recoveryPointDto: RecoveryPointDto
  isRealTime: boolean
}

function rpSortFunction(l: RecoveryPointDto, r: RecoveryPointDto) {
  return l.timestamp.valueOf() - r.timestamp.valueOf()
}

@Component({
  selector: 'rp-range-slider',
  templateUrl: './rp-range-slider.component.html',
  styleUrls: ['./rp-range-slider.component.scss'],
  standalone: false,
})
export class RpRangeSliderComponent implements OnInit {
  @ViewChild('rangeControl', { static: true }) rangeControl: ElementRef
  @Input() recoveryPoints: Array<RecoveryPointDto> = null
  @Input() enableRealTimeRecovery: boolean = null
  @Output() update = new EventEmitter<RpSliderChangedEvent>()

  private _announcedRpId: string = null
  private _announcedRealTime: boolean = null
  private _value: number = -1

  constructor() {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    // When the list of recovery points changes, we need to adjust the slider to the "right" recovery point based on
    // where it was previously set.  The rules are:
    //   1. If we were previously recovering to "now" and we can still recovery to "now", choose the latest recovery point and "now".
    //   2. Else, if the previous recovery point is still in the list, choose that recovery point.
    //   3. Else, re-initialize to the latest recovery point (this is what we do the first time through as well)

    if ('recoveryPoints' in changes) {
      // We'll get changes frequently because while the list of recoveryPoints hasn't changed, the array holding them is a
      // new object.  As such, we need to compare the previous and current recovery point IDs to see if something has really
      // changed.
      let hasChanges = true
      if (!!changes.recoveryPoints.previousValue) {
        const prev = changes.recoveryPoints.previousValue as Array<RecoveryPointDto>
        if (!!prev) {
          const prevSet = new Set(prev.map((rp) => rp.recoveryPointId))
          const curr = changes.recoveryPoints.currentValue as Array<RecoveryPointDto>
          const currSet = new Set(curr.map((rp) => rp.recoveryPointId))
          if (equalSets(prevSet, currSet)) {
            hasChanges = false
          }
        }
      }

      if (hasChanges) {
        let announceValue = this.maxValue // case 1 & 3 both emit the max value

        // identify case 2 and compute the right value
        if (!!changes.recoveryPoints.previousValue && this._announcedRealTime === false) {
          // search for previously announced recovery point ID
          const announcedRpIndex = this.sortedRecoveryPoints.findIndex(
            (rpDto) => rpDto.recoveryPointId === this._announcedRpId
          )
          if (announcedRpIndex >= 0) {
            announceValue = announcedRpIndex
          }
        }

        // We announce values asynchronously with setTimeout() so that when the parent component
        // handles the event by modifying its state, we don't hit the dreaded Angular
        // "NG0100: Expression has changed after it was checked" error.
        setTimeout(() => this.announceValue(announceValue), 0)

        // Finally, set the correct value on the range control
        this.rangeControl.nativeElement.value = `${announceValue}`
      }
    }
  }

  get sortedRecoveryPoints() {
    return this.recoveryPoints.sort(rpSortFunction)
  }

  get maxValue() {
    if (this.enableRealTimeRecovery) {
      return this.recoveryPoints.length
    }
    return this.recoveryPoints.length - 1
  }

  get value() {
    if (this._value < 0) {
      return `${this.maxValue}`
    }
    return `${this._value}`
  }

  get lastRecoveryPointIndex() {
    return this.recoveryPoints.length - 1
  }

  private get index() {
    return parseInt(this.rangeControl.nativeElement.value)
  }

  private get nowIsSelected() {
    return this.index === this.recoveryPoints.length
  }

  sliderChange() {
    this.announceValue(this.index)
  }

  private announceValue(index: number) {
    let isRealTime = false

    // store the announced value so we can set the correct location on the slider
    this._value = index

    if (index == this.recoveryPoints.length) {
      // they've selected 1 past the number of recovery points, meaning they want real time.
      index -= 1
      isRealTime = true
    }

    const rpDto = this.sortedRecoveryPoints[index]
    this._announcedRpId = rpDto.recoveryPointId
    this._announcedRealTime = isRealTime

    this.update.emit({
      recoveryPointDto: rpDto,
      isRealTime: isRealTime,
    })
  }
}
